import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT, documentTextButtonSx } from '../constants/documentTheme';
import StripeCheckout from './StripeCheckout';

const TripPackageAddOns = ({ tripId, onPurchased }) => {
  const { t } = useTranslation();
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [buyingId, setBuyingId] = useState(null);
  const [checkout, setCheckout] = useState(null);

  const fetchLedger = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/package-booking/trips/${tripId}/addons`);
      setLedger(response.data);
      return response.data;
    } catch (err) {
      if (err.response?.status === 404) {
        setLedger(null);
        return null;
      }
      setError(err.response?.data?.error || t('tripAddOns.loadFailed'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [tripId, t]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  const buyOpenTab = async () => {
    try {
      setBuyingId('tab');
      setError('');
      const response = await api.post(`/package-booking/trips/${tripId}/addons/checkout-tab`);
      if (response.data?.clientSecret) {
        setCheckout({
          clientSecret: response.data.clientSecret,
          title: t('tripAddOns.payTabTitle'),
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || t('tripAddOns.buyFailed'));
    } finally {
      setBuyingId(null);
    }
  };

  const buyAddOn = async (packageItemId) => {
    try {
      setBuyingId(packageItemId);
      setError('');
      const response = await api.post(`/package-booking/trips/${tripId}/addons/${packageItemId}/checkout`);
      if (response.data?.clientSecret) {
        setCheckout({
          clientSecret: response.data.clientSecret,
          title: response.data.item?.name || t('tripAddOns.title'),
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || t('tripAddOns.buyFailed'));
    } finally {
      setBuyingId(null);
    }
  };

  const handleCheckoutSuccess = async () => {
    setCheckout(null);
    await fetchLedger();
    if (onPurchased) onPurchased();
  };

  const buyOffer = async (offer) => {
    const busyKey = `offer-${offer.id}`;
    try {
      setBuyingId(busyKey);
      setError('');
      const response = await api.post(`/package-booking/trips/${tripId}/addons/offers/${offer.id}/checkout`);
      if (response.data?.claimed) {
        await handleCheckoutSuccess();
      } else if (response.data?.clientSecret) {
        setCheckout({
          clientSecret: response.data.clientSecret,
          title: response.data.item?.name || offer.name,
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || t('tripAddOns.buyFailed'));
    } finally {
      setBuyingId(null);
    }
  };

  const renderAddonRow = (item) => (
    <Box
      key={item.key}
      data-testid={item.testId}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        borderBottom: `1px solid ${DOCUMENT.rule}`,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15 }}>{item.name}</Typography>
        {item.description ? (
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>{item.description}</Typography>
        ) : null}
        <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
          {[
            item.dayNumber ? t('tripAddOns.day', { n: item.dayNumber }) : null,
            item.status === 'paid'
              ? t('tripAddOns.paid')
              : item.status === 'unpaid'
                ? t('tripAddOns.pending')
                : null,
          ].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {item.free
            ? t('tripAddOns.complimentary')
            : `${item.amount.toFixed(0)} ${item.currency}`}
        </Typography>
        {item.paymentLinkUrl ? (
          <Button
            component="a"
            href={item.paymentLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={documentTextButtonSx}
            data-testid={`${item.testId}-pay-link`}
          >
            {item.paymentLinkLabel || t('tripAddOns.payWithLink')}
          </Button>
        ) : null}
        {item.status !== 'paid' && (
          <Button
            sx={documentTextButtonSx}
            disabled={Boolean(buyingId)}
            onClick={item.onBuy}
            data-testid={`${item.testId}-buy`}
          >
            {buyingId === item.busyKey ? t('tripAddOns.buying') : t('tripAddOns.buy')}
          </Button>
        )}
      </Box>
    </Box>
  );

  const catalog = (ledger?.catalog || []).map((item) => ({
    key: `package-${item.id}`,
    testId: `trip-addon-${item.id}`,
    busyKey: item.id,
    name: item.name,
    dayNumber: item.day_number,
    status: item.status,
    amount: Number(item.additional_cost),
    currency: item.currency,
    free: false,
    onBuy: () => buyAddOn(item.id),
  }));
  const mapOffer = (offer) => ({
    key: `offer-${offer.id}`,
    testId: `trip-addon-offer-${offer.id}`,
    busyKey: `offer-${offer.id}`,
    name: offer.name,
    description: offer.description,
    dayNumber: offer.day_number,
    status: offer.status,
    amount: Number(offer.amount),
    currency: offer.currency,
    free: Boolean(offer.free),
    paymentLinkUrl: offer.payment_link_url || null,
    paymentLinkLabel: offer.payment_link_label || '',
    onBuy: () => buyOffer(offer),
    audience: offer.audience === 'personal' ? 'personal' : 'shared',
  });
  const offers = (ledger?.offers || []).map(mapOffer);
  const sharedOffers = offers.filter((offer) => offer.audience !== 'personal');
  const personalOffers = offers.filter((offer) => offer.audience === 'personal');
  const showOfferSections = personalOffers.length > 0;
  const rows = showOfferSections ? catalog : [...catalog, ...offers];
  const totals = ledger?.totals || {};
  const dueCount = Number(totals.dueCount || 0);
  const unpaidTotal = Number(totals.unpaidTotal || 0);
  return (
    <Box data-testid="trip-package-addons">
      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
        {t('tripAddOns.title')}
      </Typography>
      <Typography sx={{ fontSize: 14, color: DOCUMENT.ink3, mb: 2.5 }}>
        {t('tripAddOns.subtitle')}
      </Typography>
      {dueCount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
            {t('tripAddOns.tabDue', { count: dueCount, amount: unpaidTotal.toFixed(0) })}
          </Typography>
          <Button
            data-testid="trip-addon-pay-tab"
            sx={documentTextButtonSx}
            disabled={Boolean(buyingId)}
            onClick={buyOpenTab}
          >
            {buyingId === 'tab' ? t('tripAddOns.buying') : t('tripAddOns.payTab')}
          </Button>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Typography sx={{ color: DOCUMENT.ink3 }}>{t('common.loading')}</Typography>
      ) : rows.length === 0 && sharedOffers.length === 0 && personalOffers.length === 0 && dueCount === 0 ? (
        <Typography sx={{ color: DOCUMENT.ink3 }}>{t('tripAddOns.empty')}</Typography>
      ) : (
        <>
          {rows.map((item) => renderAddonRow(item))}
          {showOfferSections && (
            <>
              {sharedOffers.length > 0 && (
                <Box data-testid="trip-addons-shared" sx={{ mt: rows.length ? 2 : 0 }}>
                  <Typography sx={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.5 }}>
                    {t('tripAddOns.common')}
                  </Typography>
                  {sharedOffers.map((item) => renderAddonRow(item))}
                </Box>
              )}
              <Box data-testid="trip-addons-personal" sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.5 }}>
                  {t('tripAddOns.individual')}
                </Typography>
                {personalOffers.map((item) => renderAddonRow(item))}
              </Box>
            </>
          )}
        </>
      )}
      <StripeCheckout
        open={Boolean(checkout?.clientSecret)}
        clientSecret={checkout?.clientSecret}
        tripTitle={checkout?.title}
        onClose={() => setCheckout(null)}
        onSuccess={handleCheckoutSuccess}
      />
    </Box>
  );
};

export default TripPackageAddOns;
